import { Grid, Typography } from '@mui/material';
import { PieChart } from '@tassat/chart';
import { formatAsAsset } from '@tassat/utility';
import Decimal from 'decimal.js';
import React from 'react';

import { useAppSelector } from 'Reducers/store/hooks';
import { getAccountPositionsStats } from 'Selectors/accountPositions';

import { muiStyles } from './styles';
import { AssetBalancesChartProps } from './types';

const AssetBalancesChart: React.FC<AssetBalancesChartProps> = (props) => {
    const classes = muiStyles(props);
    const accountBalancesStats = useAppSelector((state) => getAccountPositionsStats(state, true, false));
    const chartData = React.useMemo(() => {
        let total = new Decimal(0.0);

        const data = accountBalancesStats.reduce((acc, curr) => {
            Object.values(curr.ccy)
                .forEach((currency) => {
                    acc.push({
                        label: currency.asset?.Curcode || 'UNK',
                        value: currency.currentBalance.toNumber(),
                        text: formatAsAsset(currency.currentBalance, currency.asset, 'symbol')
                    });
                    total = Decimal.add(total, currency.currentBalance)
                        .toNumber();
                });

            Object.values(curr.security)
                .forEach((asset) => {
                    acc.push({
                        label: asset.asset?.Ticker || 'UNK',
                        value: asset.equivalentCurrentBalance.toNumber(),
                        text: formatAsAsset(asset.equivalentCurrentBalance, curr.ccy[Object.keys(curr.ccy)[0]]?.asset, 'symbol')
                    });
                    total = Decimal.add(total, asset.equivalentCurrentBalance)
                        .toNumber();
                });

            return acc;
        }, []);

        // FRED-3150: Don't show slices that are less than .01% of the total to not break plotly
        // Use decimal.js since we are doing multiplication/division
        return data.filter((dataPoint) => {
            return new Decimal(dataPoint.value)
                .div(total)
                .mul(100)
                .gt(0.01);
        });
    }, [accountBalancesStats]);

    const Caption = () => {
        return (
            <Grid
                container
                direction='column'
                wrap='nowrap'
            >
                <Grid item>
                    <Typography
                        className={classes.captionFont}
                        variant='body2'
                    >
                        Percentages based on current balances.
                    </Typography>
                </Grid>
                <Grid item>
                    <Typography
                        className={classes.captionFont}
                        variant='body2'
                    >
                        Asset allocations less than 0.01% will not be displayed.
                    </Typography>
                </Grid>
            </Grid>
        );
    };

    return (
        <Grid
            className={classes.root}
            container
            direction='column'
        >
            <PieChart
                {...props}
                Caption={Caption}
                chartColors={[
                    '#044789',
                    '#88C9BF',
                    '#7399C6'
                ]}
                data={chartData}
                hovertemplate='<span>%{label}</span><br><span>%{text}</span><br><span>%{percent:.2%}</span><extra></extra>'
                noDataCaption='Asset allocations will be displayed after an account is funded'
                rootStyles={{ borderRadius: 4 }}
                texttemplate='<span>%{percent:.2%}</span>'
                title='Asset Allocation'
            />
        </Grid>
    );
};

export default AssetBalancesChart;